import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { Toolbar, TableShell, FormModal, TextField, SelectField } from "@/components/FormKit";
import { Pencil, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", email: "", phone: "", role: "", profile_code: "FINANCEIRO", password: "", status: "ativo" };

const RESET_GROUPS = [
  { key: "financial_accounts", label: "Contas Financeiras", cadastro: true },
  { key: "suppliers", label: "Fornecedores", cadastro: true },
  { key: "financial_categories", label: "Categorias", cadastro: true },
  { key: "cost_centers", label: "Centros de Custo", cadastro: true },
  { key: "accounts_payable", label: "Contas a Pagar (+ movimentações)" },
  { key: "accounts_receivable", label: "Contas a Receber (+ movimentações)" },
  { key: "transfers_only", label: "Somente Transferências Internas" },
  { key: "financial_transactions", label: "Todas as Movimentações (baixas + transferências)" },
  { key: "statements", label: "Prestações de Contas" },
  { key: "budgets", label: "Orçamentos" },
  { key: "audit_logs", label: "Auditoria" },
];

export default function UsersPage() {
  const { hasPermission } = useAuth();
  const [resetting, setResetting] = useState(false);
  const [resetSel, setResetSel] = useState([]);
  const isAdmin = hasPermission("*");

  const toggleReset = (key) => setResetSel((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const selectCadastros = () => setResetSel(RESET_GROUPS.filter((g) => g.cadastro).map((g) => g.key));
  const selectAllReset = () => setResetSel(RESET_GROUPS.map((g) => g.key));

  const resetData = async () => {
    if (resetSel.length === 0) { toast.error("Selecione ao menos um tipo de dado para excluir."); return; }
    const labels = RESET_GROUPS.filter((g) => resetSel.includes(g.key)).map((g) => g.label).join(", ");
    const typed = window.prompt(
      `Esta ação vai APAGAR de forma IRREVERSÍVEL:\n\n• ${RESET_GROUPS.filter((g) => resetSel.includes(g.key)).map((g) => g.label).join("\n• ")}\n\nUsuários e perfis serão mantidos.\n\nDigite ZERAR para confirmar:`
    );
    if (typed == null) return;
    if (typed.trim().toUpperCase() !== "ZERAR") { toast.error("Confirmação inválida."); return; }
    setResetting(true);
    try {
      await api.post("/admin/reset-data", { confirm: "ZERAR", groups: resetSel });
      toast.success(`Exclusão concluída: ${labels}.`);
      setTimeout(() => window.location.reload(), 900);
    } catch (err) { toast.error(apiError(err)); } finally { setResetting(false); }
  };

  const [recomputing, setRecomputing] = useState(false);
  const recomputeBalances = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/admin/recompute-balances");
      toast.success(`Saldos recalculados: ${data.updated} de ${data.accounts} conta(s) ajustada(s).`);
    } catch (err) { toast.error(apiError(err)); } finally { setRecomputing(false); }
  };

  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => { setLoading(true); api.get("/users").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/profiles").then((r) => setProfiles(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success("Usuário salvo."); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  const profileOptions = profiles.map((p) => ({ value: p.code, label: p.name }));
  const profileName = (code) => profiles.find((p) => p.code === code)?.name || code;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => [r.name, r.email, r.role].some((v) => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <div className="space-y-6">
      <PageHeader title="Usuários" subtitle="Gestão de usuários e acessos ao sistema" />
      <Toolbar search={search} setSearch={setSearch} searchTestId="users-search"
        onNew={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} newLabel="Novo Usuário" newTestId="add-user-button" />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : filtered.length === 0 ? <EmptyState text="Nenhum usuário encontrado." />
        : (
        <TableShell testId="users-table" headers={[{ label: "Nome" }, { label: "E-mail" }, { label: "Cargo" }, { label: "Perfil" }, { label: "Último Acesso" }, { label: "Status" }, { label: "Ações", align: "right" }]}>
          {filtered.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`user-row-${r.id}`}>
              <td className="px-4 py-3.5 font-medium text-slate-800">{r.name}</td>
              <td className="px-4 py-3.5 text-slate-600">{r.email}</td>
              <td className="px-4 py-3.5 text-slate-500">{r.role || "—"}</td>
              <td className="px-4 py-3.5"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-brand-light text-brand border border-brand/10">{profileName(r.profile_code)}</span></td>
              <td className="px-4 py-3.5 text-slate-500 text-xs">{r.last_login ? fmtDate(r.last_login) : "Nunca"}</td>
              <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3.5 text-right">
                <button onClick={() => { setEditing(r); setForm({ ...EMPTY, ...r, password: "" }); setOpen(true); }} data-testid={`edit-user-${r.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <FormModal open={open} onOpenChange={setOpen} title={editing ? "Editar Usuário" : "Novo Usuário"} onSubmit={submit} submitting={submitting} testId="user-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Nome" value={form.name} onChange={(v) => set("name", v)} testId="user-name" required className="sm:col-span-2" />
          <TextField label="E-mail" type="email" value={form.email} onChange={(v) => set("email", v)} testId="user-email" required />
          <TextField label="Telefone" value={form.phone} onChange={(v) => set("phone", v)} testId="user-phone" />
          <TextField label="Cargo" value={form.role} onChange={(v) => set("role", v)} testId="user-role" />
          <SelectField label="Perfil" value={form.profile_code} onChange={(v) => set("profile_code", v)} testId="user-profile" options={profileOptions} />
          <TextField label={editing ? "Nova Senha (deixe em branco para manter)" : "Senha"} type="password" value={form.password} onChange={(v) => set("password", v)} testId="user-password" required={!editing} />
          <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} testId="user-status" options={[{ value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]} />
        </div>
      </FormModal>

      {isAdmin && (
        <div className="mt-8 bg-white rounded-xl border border-rose-200 shadow-sm p-5" data-testid="danger-zone">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5" /></div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-slate-900">Zona de perigo — Excluir dados</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                Selecione <strong>o que deseja excluir</strong>. <strong>Usuários e perfis são sempre mantidos.</strong> Esta ação é irreversível.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button onClick={recomputeBalances} disabled={recomputing} data-testid="recompute-balances-button"
                  className="text-xs font-medium px-3 py-2 rounded-md border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-1.5 disabled:opacity-50">
                  {recomputing && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Recalcular saldos das contas
                </button>
                <span className="text-xs text-slate-400">Corrige os saldos a partir das movimentações (não apaga nada).</span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={selectCadastros} data-testid="reset-select-cadastros" className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Só Cadastros</button>
                <button onClick={selectAllReset} data-testid="reset-select-all" className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Selecionar tudo</button>
                {resetSel.length > 0 && <button onClick={() => setResetSel([])} data-testid="reset-clear" className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50">Limpar</button>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 max-w-2xl">
                {RESET_GROUPS.map((g) => (
                  <label key={g.key} data-testid={`reset-group-${g.key}`} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${resetSel.includes(g.key) ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    <input type="checkbox" checked={resetSel.includes(g.key)} onChange={() => toggleReset(g.key)} className="accent-rose-600" data-testid={`reset-check-${g.key}`} />
                    {g.label}
                  </label>
                ))}
              </div>
            </div>
            <button onClick={resetData} disabled={resetting || resetSel.length === 0} data-testid="reset-data-button"
              className="bg-rose-600 hover:bg-rose-700 text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 shrink-0 self-start">
              {resetting && <Loader2 className="h-4 w-4 animate-spin" />} Excluir selecionados
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
